import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ShieldAlert, RefreshCcw, Check, X, Search, Edit3, History, Loader2, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { apiGet, apiPost } from "@/lib/api";

/**
 * Admin → Data Quality Monitor
 *
 * Three tabs:
 *   1. Suspicious Changes — pending TV-rejected values awaiting decision.
 *      Each row offers Accept / Keep / Investigate / Override Manually.
 *   2. Auto-Corrected — read-only history of values the audit job
 *      rolled back automatically.
 *   3. Audit History — list of audit runs with summary counts and a
 *      "Run Audit Now" button that re-scans every active CSE security.
 *
 * Designed for high-density tables with quick decision buttons. The
 * Override Manually dialog accepts EPS/PE/PB/ROE/Yield, applies via
 * POST /admin/data-quality/{symbol}/manual, and tags the row
 * `manually_verified=true` so the scheduler stops overwriting it.
 */

const fmtNum = (v, digits = 2) => {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

// --------- Override-Manually dialog ----------------------------------------
const ManualOverrideDialog = ({ open, onClose, symbol, defaultReason = null, onSuccess }) => {
  const [form, setForm] = useState({
    cse_symbol: "",
    eps: "", pe_ratio: "", pb_ratio: "", roe: "", dividend_yield: "", dividend_per_share: "",
    notes: "",
    reason_category: defaultReason || "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        cse_symbol: symbol || "",
        eps: "", pe_ratio: "", pb_ratio: "", roe: "", dividend_yield: "", dividend_per_share: "",
        notes: "",
        reason_category: defaultReason || "",
      });
    }
  }, [open, symbol, defaultReason]);

  const targetSymbol = symbol || form.cse_symbol.trim().toUpperCase();

  const submit = async () => {
    if (!targetSymbol) {
      toast.error("Enter a CSE symbol (e.g. JINS.N0000)");
      return;
    }
    const body = {};
    for (const k of ["eps", "pe_ratio", "pb_ratio", "roe", "dividend_yield", "dividend_per_share"]) {
      if (form[k] !== "" && form[k] !== null) {
        const n = Number(form[k]);
        if (!Number.isNaN(n)) body[k] = n;
      }
    }
    if (form.notes) body.notes = form.notes;
    if (form.reason_category) body.reason_category = form.reason_category;
    if (Object.keys(body).filter((k) => k !== "notes" && k !== "reason_category").length === 0) {
      toast.error("Enter at least one numeric field (EPS, PE, PB, ROE, or Yield)");
      return;
    }
    setBusy(true);
    try {
      const res = await apiPost(`/admin/data-quality/${encodeURIComponent(targetSymbol)}/manual`, body);
      toast.success(`${targetSymbol} corrected`, { description: `Applied: ${Object.keys(res.applied).join(", ")}` });
      onSuccess?.();
      onClose?.();
    } catch (e) {
      toast.error("Override failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="dq-manual-dialog">
        <DialogHeader>
          <DialogTitle>Override fundamentals — {targetSymbol || "(enter a symbol)"}</DialogTitle>
          <DialogDescription>
            Hand-correct values from the official annual report or a CSE corporate-action announcement.
            The row will be tagged
            <code className="mx-1 px-1 rounded bg-muted text-xs">manually_verified</code>
            and the scheduler will stop overwriting it until you clear the lock.
          </DialogDescription>
        </DialogHeader>
        {!symbol && (
          <div className="space-y-1.5">
            <Label htmlFor="dq-symbol" className="text-xs">CSE symbol</Label>
            <Input
              id="dq-symbol"
              placeholder="JINS.N0000"
              value={form.cse_symbol}
              onChange={(e) => setForm((f) => ({ ...f, cse_symbol: e.target.value }))}
              data-testid="dq-manual-symbol"
            />
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="text-xs">Reason</Label>
          <div className="flex gap-2 flex-wrap">
            {[
              { v: "corporate_action", label: "Corporate action (rights / split / bonus)" },
              { v: "data_quality_fix", label: "Data quality fix" },
              { v: "", label: "Unspecified" },
            ].map((opt) => (
              <button
                type="button"
                key={opt.v || "none"}
                onClick={() => setForm((f) => ({ ...f, reason_category: opt.v }))}
                className={`text-xs px-2.5 py-1 rounded-md border transition-colors ${
                  form.reason_category === opt.v
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-border hover:bg-muted"
                }`}
                data-testid={`dq-reason-${opt.v || "none"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {[
            { k: "eps", label: "EPS (Rs)", placeholder: "5.03" },
            { k: "pe_ratio", label: "PE", placeholder: "9.99" },
            { k: "pb_ratio", label: "PB", placeholder: "2.06" },
            { k: "roe", label: "ROE (%)", placeholder: "20.27" },
            { k: "dividend_yield", label: "Dividend Yield (%)", placeholder: "5.0" },
            { k: "dividend_per_share", label: "DPS (Rs)", placeholder: "2.50" },
          ].map(({ k, label, placeholder }) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={`dq-${k}`} className="text-xs">{label}</Label>
              <Input
                id={`dq-${k}`}
                inputMode="decimal"
                placeholder={placeholder}
                value={form[k]}
                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                data-testid={`dq-manual-${k}`}
              />
            </div>
          ))}
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="dq-notes" className="text-xs">Notes (optional)</Label>
          <Textarea
            id="dq-notes"
            placeholder={
              form.reason_category === "corporate_action"
                ? "e.g. 3:1 rights issue Jan 2026 (CSE announcement #123). Adjusted EPS = previous EPS / 1.333"
                : "e.g. Verified from official annual report 2025"
            }
            rows={2}
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
            data-testid="dq-manual-notes"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="dq-manual-submit">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving</> : "Apply override"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// --------- Suspicious Changes table ---------------------------------------
const SuspiciousTable = ({ rows, onAction, onOpenOverride, busy }) => {
  if (!rows.length) {
    return <div className="text-sm text-muted-foreground p-6 text-center" data-testid="dq-empty">No flagged changes. Nice.</div>;
  }
  return (
    <Table data-testid="dq-suspicious-table">
      <TableHeader>
        <TableRow>
          <TableHead>Company</TableHead>
          <TableHead className="text-right">Rejected EPS</TableHead>
          <TableHead className="text-right">Trusted EPS</TableHead>
          <TableHead className="text-right">Implied PE</TableHead>
          <TableHead>Reason</TableHead>
          <TableHead>Detected</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => {
          const rej = r.rejected_values || {};
          const trust = r.trusted_values || {};
          return (
            <TableRow key={r._id} data-testid={`dq-suspicious-row-${r.cse_symbol}`}>
              <TableCell className="font-medium">{r.cse_symbol}</TableCell>
              <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">{fmtNum(rej.eps)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtNum(trust.eps)}</TableCell>
              <TableCell className="text-right tabular-nums">{r.implied_pe == null ? "—" : fmtNum(r.implied_pe)}</TableCell>
              <TableCell className="max-w-[260px]"><span className="text-xs text-muted-foreground line-clamp-2">{r.reason}</span></TableCell>
              <TableCell className="text-xs text-muted-foreground">{fmtDateTime(r.detected_at)}</TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <div className="inline-flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => onAction(r._id, "accept")} disabled={busy} data-testid={`dq-accept-${r.cse_symbol}`}>
                    <Check className="h-3.5 w-3.5 mr-1" /> Accept
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onAction(r._id, "keep")} disabled={busy} data-testid={`dq-keep-${r.cse_symbol}`}>
                    <X className="h-3.5 w-3.5 mr-1" /> Keep
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => onAction(r._id, "investigate")} disabled={busy} data-testid={`dq-investigate-${r.cse_symbol}`}>
                    <Search className="h-3.5 w-3.5 mr-1" /> Investigate
                  </Button>
                  <Button size="sm" onClick={() => onOpenOverride(r.cse_symbol)} disabled={busy} data-testid={`dq-override-${r.cse_symbol}`}>
                    <Edit3 className="h-3.5 w-3.5 mr-1" /> Override
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

// --------- Auto-Corrected table -------------------------------------------
// --------- Corporate Action Suspects (Needs Manual EPS Update) -----------
// 2026-05-09 — Lists stocks where the merge flagged a likely
// split/rights/bonus but neither AR nor TV had a safe alternative EPS.
// Admin must look up the correct adjusted EPS on the CSE website /
// CSEPal and enter it via the override dialog.
const CorporateActionSuspectsTable = ({ rows, onOpenOverride, busy }) => {
  if (!rows.length) {
    return (
      <div className="text-sm text-muted-foreground p-6 text-center">
        No stocks currently need a manual EPS update. The weekly Monday
        scanner will flag any new corporate actions automatically.
      </div>
    );
  }
  return (
    <Table data-testid="dq-needs-eps-table">
      <TableHeader>
        <TableRow>
          <TableHead>Symbol</TableHead>
          <TableHead className="text-right">Price</TableHead>
          <TableHead className="text-right">EPS used</TableHead>
          <TableHead className="text-right">PE used</TableHead>
          <TableHead className="text-right">AR EPS</TableHead>
          <TableHead className="text-right">AR PE</TableHead>
          <TableHead className="text-right">TV EPS</TableHead>
          <TableHead className="text-right">TV PE</TableHead>
          <TableHead>Signal</TableHead>
          <TableHead className="text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.cse_symbol} data-testid={`dq-needs-eps-row-${r.cse_symbol}`}>
            <TableCell className="font-medium font-mono">{r.cse_symbol}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtNum(r.current_price)}</TableCell>
            <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">{fmtNum(r.eps_used)}</TableCell>
            <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">{r.pe_used != null ? `${fmtNum(r.pe_used)}x` : "—"}</TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground">{fmtNum(r.ar_eps)}</TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground">{r.ar_pe != null ? `${fmtNum(r.ar_pe)}x` : "—"}</TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground">{fmtNum(r.tv_eps)}</TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground">{r.tv_pe != null ? `${fmtNum(r.tv_pe)}x` : "—"}</TableCell>
            <TableCell>
              <Badge
                variant="outline"
                className="border-amber-500/60 bg-amber-500/10 text-amber-700 dark:text-amber-300"
              >
                {r.signal || "WATCH"}
              </Badge>
            </TableCell>
            <TableCell className="text-right">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onOpenOverride(r.cse_symbol)}
                disabled={busy}
                data-testid={`dq-needs-eps-override-${r.cse_symbol}`}
              >
                Override EPS
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};


// --------- OCR Re-extraction Required ------------------------------------
// 2026-05-09 — Stocks whose Claude OCR extracted bad units (net_profit
// < Rs.10M, missing shares, etc.) despite is_analysed=true. Different
// from "Needs Manual EPS" — these need a full PDF re-download + re-OCR
// (the v3 prompt + validate_extraction guard will get it right this
// time) rather than just an EPS override.
const OcrReextractRequiredTable = ({ rows, onReextract, busy }) => {
  if (!rows.length) {
    return (
      <div className="text-sm text-muted-foreground p-6 text-center">
        No stocks currently need OCR re-extraction. Future runs will use
        the v3 prompt with strict unit rules + post-extraction validation.
      </div>
    );
  }
  return (
    <Table data-testid="dq-needs-reocr-table">
      <TableHeader>
        <TableRow>
          <TableHead>Company</TableHead>
          <TableHead>FY</TableHead>
          <TableHead className="text-right">Net Profit</TableHead>
          <TableHead className="text-right">Shares Outstanding</TableHead>
          <TableHead className="text-right">EPS</TableHead>
          <TableHead>Warnings</TableHead>
          <TableHead className="text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.company_code} data-testid={`dq-needs-reocr-row-${r.company_code}`}>
            <TableCell className="font-medium font-mono">{r.company_code}</TableCell>
            <TableCell className="tabular-nums">{r.financial_year || "—"}</TableCell>
            <TableCell className="text-right tabular-nums text-red-600 dark:text-red-400">{fmtNum(r.net_profit)}</TableCell>
            <TableCell className="text-right tabular-nums text-red-600 dark:text-red-400">{r.shares_outstanding != null ? fmtNum(r.shares_outstanding) : "missing"}</TableCell>
            <TableCell className="text-right tabular-nums">{fmtNum(r.eps)}</TableCell>
            <TableCell className="text-xs max-w-[280px]">
              {(r.extraction_warnings || []).slice(0,2).map((w, i) => (
                <div key={i} className="text-amber-600 dark:text-amber-400 truncate" title={w}>• {w}</div>
              ))}
              {!r.extraction_warnings?.length && r.requires_manual_review && (
                <span className="text-amber-600 dark:text-amber-400">Manual review required</span>
              )}
            </TableCell>
            <TableCell className="text-right">
              <Button
                size="sm"
                variant="outline"
                onClick={() => onReextract(r.company_code)}
                disabled={busy}
                data-testid={`dq-needs-reocr-action-${r.company_code}`}
              >
                <RefreshCcw className="h-3 w-3 mr-1" />
                Re-download & Re-OCR
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};


const AutoCorrectedTable = ({ rows }) => {
  if (!rows.length) {
    return <div className="text-sm text-muted-foreground p-6 text-center">No auto-corrections recorded yet.</div>;
  }
  return (
    <Table data-testid="dq-autocorrected-table">
      <TableHeader>
        <TableRow>
          <TableHead>Company</TableHead>
          <TableHead className="text-right">Bad EPS (rejected)</TableHead>
          <TableHead className="text-right">Reverted to EPS</TableHead>
          <TableHead className="text-right">Implied PE</TableHead>
          <TableHead>Reason</TableHead>
          <TableHead>Resolved</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => {
          const rej = r.rejected_values || {};
          const trust = r.trusted_values || {};
          return (
            <TableRow key={r._id}>
              <TableCell className="font-medium">{r.cse_symbol}</TableCell>
              <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">{fmtNum(rej.eps)}</TableCell>
              <TableCell className="text-right tabular-nums text-emerald-600 dark:text-emerald-400">{fmtNum(trust.eps)}</TableCell>
              <TableCell className="text-right tabular-nums">{fmtNum(r.implied_pe)}</TableCell>
              <TableCell className="max-w-[280px]"><span className="text-xs text-muted-foreground line-clamp-2">{r.reason}</span></TableCell>
              <TableCell className="text-xs text-muted-foreground">{fmtDateTime(r.resolved_at || r.detected_at)}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

// --------- Corporate Action / Manual Overrides table ----------------------
const ManualOverridesTable = ({ rows, onClearLock, busy, onAddNew }) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          Active manual overrides where an admin has hand-corrected the
          fundamentals row. Use this for post-annual-report corporate actions
          (rights issues, splits, bonus shares) where the audited annual
          report's EPS pre-dates the action.
        </p>
        <Button size="sm" onClick={onAddNew} data-testid="dq-overrides-add">
          <Edit3 className="h-3.5 w-3.5 mr-1.5" /> New override
        </Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-sm text-muted-foreground p-6 text-center" data-testid="dq-overrides-empty">
          No manual overrides active.
        </div>
      ) : (
        <Table data-testid="dq-overrides-table">
          <TableHeader>
            <TableRow>
              <TableHead>Symbol</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="text-right">Original EPS</TableHead>
              <TableHead className="text-right">Override EPS</TableHead>
              <TableHead className="text-right">PE</TableHead>
              <TableHead>AR Year</TableHead>
              <TableHead>Notes</TableHead>
              <TableHead>By</TableHead>
              <TableHead>When</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const cur = r.current_values || {};
              const orig = r.original_values_before_override || {};
              const reason = r.reason_category || "—";
              return (
                <TableRow key={r.cse_symbol} data-testid={`dq-override-row-${r.cse_symbol}`}>
                  <TableCell className="font-medium">{r.cse_symbol}</TableCell>
                  <TableCell>
                    <Badge
                      variant="outline"
                      className={
                        reason === "corporate_action"
                          ? "border-emerald-600/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                          : reason === "data_quality_fix"
                          ? "border-sky-600/40 bg-sky-500/10 text-sky-700 dark:text-sky-300"
                          : ""
                      }
                    >
                      {reason === "corporate_action" ? "Corporate action"
                        : reason === "data_quality_fix" ? "Data quality fix"
                        : "—"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{fmtNum(orig.eps)}</TableCell>
                  <TableCell className="text-right tabular-nums text-emerald-600 dark:text-emerald-400 font-medium">{fmtNum(cur.eps)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtNum(cur.pe_ratio)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.annual_report_year || "—"}</TableCell>
                  <TableCell className="max-w-[260px]"><span className="text-xs text-muted-foreground line-clamp-2">{r.notes || "—"}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.actor || "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDateTime(r.at)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onClearLock(r.cse_symbol)}
                      disabled={busy}
                      data-testid={`dq-override-clear-${r.cse_symbol}`}
                    >
                      <X className="h-3.5 w-3.5 mr-1" /> Remove
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

// --------- Audit history table --------------------------------------------
const AuditHistoryTable = ({ rows }) => {
  if (!rows.length) {
    return <div className="text-sm text-muted-foreground p-6 text-center">No audit runs yet. Click <em>Run Audit Now</em>.</div>;
  }
  return (
    <Table data-testid="dq-audit-history-table">
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead className="text-right">Total checked</TableHead>
          <TableHead className="text-right">Suspicious found</TableHead>
          <TableHead className="text-right">Auto-corrected</TableHead>
          <TableHead className="text-right">Locked (skipped)</TableHead>
          <TableHead className="text-right">Duration</TableHead>
          <TableHead>Status</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r._id}>
            <TableCell className="text-sm">{fmtDateTime(r.started_at)}</TableCell>
            <TableCell className="text-right tabular-nums">{r.total_checked}</TableCell>
            <TableCell className="text-right tabular-nums text-amber-600 dark:text-amber-400">{r.suspicious_found}</TableCell>
            <TableCell className="text-right tabular-nums text-emerald-600 dark:text-emerald-400">{r.auto_corrected}</TableCell>
            <TableCell className="text-right tabular-nums text-muted-foreground">{r.skipped_manually_verified}</TableCell>
            <TableCell className="text-right tabular-nums text-xs text-muted-foreground">{r.duration_seconds}s</TableCell>
            <TableCell><Badge variant="outline" className="capitalize">{r.status}</Badge></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// --------- Page ------------------------------------------------------------
const AdminDataQualityPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState("suspicious");
  const [suspicious, setSuspicious] = useState([]);
  const [auto, setAuto] = useState([]);
  const [audits, setAudits] = useState([]);
  const [overrides, setOverrides] = useState([]);
  // 2026-05-09 — Corporate-action suspects (PE < 2 with no safe TV swap).
  // Loaded from /admin/corporate-action-suspects → only the
  // needs_manual_eps_update sub-list is shown here as a dedicated tab.
  const [corporateActionSuspects, setCorporateActionSuspects] = useState([]);
  // 2026-05-09 — Stocks where OCR clearly returned bad units (net_profit
  // < Rs.10M, shares missing, etc.) despite is_analysed=true. Need PDF
  // re-download + re-OCR with the new v3 prompt — different from
  // "Corporate Action Suspects" which need EPS override only.
  const [ocrReextractRequired, setOcrReextractRequired] = useState([]);
  const [loading, setLoading] = useState({ suspicious: false, auto: false, audits: false, overrides: false, corp: false, reocr: false });
  const [actionBusy, setActionBusy] = useState(false);
  const [auditBusy, setAuditBusy] = useState(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideSymbol, setOverrideSymbol] = useState(null);
  const [overrideDefaultReason, setOverrideDefaultReason] = useState(null);

  const loadSuspicious = useCallback(async () => {
    setLoading((s) => ({ ...s, suspicious: true }));
    try {
      const res = await apiGet("/admin/data-quality/suspicious-changes?status=pending&limit=200");
      setSuspicious(res?.items || []);
    } catch (e) {
      toast.error("Failed to load suspicious changes", { description: e?.message });
    } finally {
      setLoading((s) => ({ ...s, suspicious: false }));
    }
  }, []);

  const loadAuto = useCallback(async () => {
    setLoading((s) => ({ ...s, auto: true }));
    try {
      const res = await apiGet("/admin/data-quality/auto-corrected?limit=200");
      setAuto(res?.items || []);
    } catch (e) {
      toast.error("Failed to load auto-corrections", { description: e?.message });
    } finally {
      setLoading((s) => ({ ...s, auto: false }));
    }
  }, []);

  const loadAudits = useCallback(async () => {
    setLoading((s) => ({ ...s, audits: true }));
    try {
      const res = await apiGet("/admin/data-quality/audit/history?limit=50");
      setAudits(res?.items || []);
    } catch (e) {
      toast.error("Failed to load audit history", { description: e?.message });
    } finally {
      setLoading((s) => ({ ...s, audits: false }));
    }
  }, []);

  const loadOverrides = useCallback(async () => {
    setLoading((s) => ({ ...s, overrides: true }));
    try {
      const res = await apiGet("/admin/data-quality/manual-overrides?limit=200");
      setOverrides(res?.items || []);
    } catch (e) {
      toast.error("Failed to load manual overrides", { description: e?.message });
    } finally {
      setLoading((s) => ({ ...s, overrides: false }));
    }
  }, []);

  // 2026-05-09 — Corporate-action suspects loader. Hits the new admin
  // endpoint and only displays the `needs_manual_eps_update` bucket
  // (stocks where AR EPS gives PE < 2 AND TV had no safer alternative).
  const loadCorporateActionSuspects = useCallback(async () => {
    setLoading((s) => ({ ...s, corp: true }));
    try {
      const res = await apiGet("/admin/corporate-action-suspects");
      setCorporateActionSuspects(res?.needs_manual_eps_update || []);
    } catch (e) {
      toast.error("Failed to load corporate-action suspects", { description: e?.message });
    } finally {
      setLoading((s) => ({ ...s, corp: false }));
    }
  }, []);

  // 2026-05-09 — Loader for the OCR re-extraction queue.
  const loadOcrReextract = useCallback(async () => {
    setLoading((s) => ({ ...s, reocr: true }));
    try {
      const res = await apiGet("/admin/ocr-reextraction-required");
      setOcrReextractRequired(res?.rows || []);
    } catch (e) {
      toast.error("Failed to load OCR re-extraction queue", { description: e?.message });
    } finally {
      setLoading((s) => ({ ...s, reocr: false }));
    }
  }, []);

  const refreshAll = useCallback(() => {
    loadSuspicious();
    loadAuto();
    loadAudits();
    loadOverrides();
    loadCorporateActionSuspects();
    loadOcrReextract();
  }, [loadSuspicious, loadAuto, loadAudits, loadOverrides, loadCorporateActionSuspects, loadOcrReextract]);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const handleAction = async (changeId, action) => {
    setActionBusy(true);
    try {
      const res = await apiPost(`/admin/data-quality/suspicious/${changeId}/${action}`, {});
      toast.success(`${res.cse_symbol} → ${res.new_status}`);
      refreshAll();
    } catch (e) {
      toast.error("Action failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setActionBusy(false);
    }
  };

  const handleClearLock = async (symbol) => {
    setActionBusy(true);
    try {
      await apiPost(`/admin/data-quality/${encodeURIComponent(symbol)}/clear-lock`, {});
      toast.success(`${symbol} — manual lock cleared`);
      refreshAll();
    } catch (e) {
      toast.error("Failed to clear lock", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setActionBusy(false);
    }
  };

  const handleOpenOverride = (symbol, reasonCategory = null) => {
    setOverrideSymbol(symbol);
    setOverrideDefaultReason(reasonCategory);
    setOverrideOpen(true);
  };

  const handleRunAudit = async () => {
    setAuditBusy(true);
    try {
      const res = await apiPost("/admin/data-quality/audit/run?auto_correct=true", {});
      toast.success("Audit complete", {
        description: `Checked ${res.total_checked} stocks · found ${res.suspicious_found} suspicious · auto-corrected ${res.auto_corrected}`,
      });
      refreshAll();
    } catch (e) {
      toast.error("Audit failed", { description: e?.response?.data?.detail || e?.message });
    } finally {
      setAuditBusy(false);
    }
  };

  const tabCounts = useMemo(() => ({
    suspicious: suspicious.length,
    auto: auto.length,
    audits: audits.length,
    overrides: overrides.length,
    corporate_actions: overrides.filter((o) => o.reason_category === "corporate_action").length,
    needs_eps_update: corporateActionSuspects.length,
    needs_reocr: ocrReextractRequired.length,
  }), [suspicious, auto, audits, overrides, corporateActionSuspects, ocrReextractRequired]);

  // 2026-05-09 — Trigger PDF re-download + re-OCR for a single company.
  const handleReextract = useCallback(async (companyCode) => {
    if (!companyCode) return;
    const proceed = window.confirm(
      `Re-download annual report PDFs for ${companyCode} and force re-OCR with the new v3 prompt?\n\n` +
      `This will reset is_analysed=false on existing reports and queue them for fresh extraction. ` +
      `Allow a few minutes for Claude to re-process.`,
    );
    if (!proceed) return;
    setActionBusy(true);
    try {
      const res = await apiPost(
        `/admin/reports/reextract?company_codes=${encodeURIComponent(companyCode)}`,
        {},
      );
      toast.success(
        `Re-extraction queued for ${companyCode}`,
        { description: `${res?.reports_reset_for_reocr || 0} reports reset; OCR will run on next worker pass.` },
      );
      await loadOcrReextract();
    } catch (e) {
      toast.error(`Re-extraction failed for ${companyCode}`, { description: e?.message });
    } finally {
      setActionBusy(false);
    }
  }, [loadOcrReextract]);

  return (
    <div className="space-y-5" data-testid="admin-data-quality-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight inline-flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-600" /> Data Quality Monitor
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Detect and correct upstream fundamentals errors before they corrupt scoring.
            Symmetric guard catches EPS swings &gt;2× and implied PE outside [2, 100].
          </p>
        </div>
        <div className="inline-flex gap-2">
          <Button variant="outline" size="sm" onClick={refreshAll} disabled={Object.values(loading).some(Boolean)} data-testid="dq-refresh">
            <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
          <Button onClick={handleRunAudit} disabled={auditBusy} data-testid="dq-run-audit">
            {auditBusy ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Running…</> : <><History className="h-3.5 w-3.5 mr-1.5" /> Run Audit Now</>}
          </Button>
        </div>
      </div>

      {suspicious.length > 0 && (
        <Card className="border-amber-500/40 bg-amber-500/5">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-sm text-amber-900 dark:text-amber-200">
              <strong>{suspicious.length} pending flag{suspicious.length === 1 ? "" : "s"}</strong> awaiting review. Cross-check
              against <a href="https://cse.lk" target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5">
              cse.lk <ExternalLink className="h-3 w-3" /></a> or CSEPal before deciding.
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Findings</CardTitle>
          <CardDescription>Click <em>Override</em> on any row to enter verified values from CSEPal / annual report.</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="suspicious" data-testid="dq-tab-suspicious">
                Suspicious Changes
                <Badge variant="secondary" className="ml-2 tabular-nums">{tabCounts.suspicious}</Badge>
              </TabsTrigger>
              <TabsTrigger value="overrides" data-testid="dq-tab-overrides">
                Manual Overrides
                <Badge variant="secondary" className="ml-2 tabular-nums">{tabCounts.overrides}</Badge>
                {tabCounts.corporate_actions > 0 && (
                  <Badge
                    variant="outline"
                    className="ml-1 tabular-nums border-emerald-600/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    title={`${tabCounts.corporate_actions} corporate-action overrides`}
                  >
                    CA {tabCounts.corporate_actions}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="needs_eps" data-testid="dq-tab-needs-eps">
                Needs Manual EPS
                <Badge
                  variant="outline"
                  className={`ml-2 tabular-nums ${tabCounts.needs_eps_update > 0 ? "border-amber-500/60 bg-amber-500/10 text-amber-700 dark:text-amber-300" : ""}`}
                >
                  {tabCounts.needs_eps_update}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="needs_reocr" data-testid="dq-tab-needs-reocr">
                OCR Re-extraction
                <Badge
                  variant="outline"
                  className={`ml-2 tabular-nums ${tabCounts.needs_reocr > 0 ? "border-red-500/60 bg-red-500/10 text-red-700 dark:text-red-300" : ""}`}
                >
                  {tabCounts.needs_reocr}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="auto" data-testid="dq-tab-auto">
                Auto-Corrected
                <Badge variant="secondary" className="ml-2 tabular-nums">{tabCounts.auto}</Badge>
              </TabsTrigger>
              <TabsTrigger value="audits" data-testid="dq-tab-audits">
                Audit History
                <Badge variant="secondary" className="ml-2 tabular-nums">{tabCounts.audits}</Badge>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="suspicious" className="pt-3">
              {/* 2026-05-09 — Explanatory note: the suspicious_changes
                  collection is the AUTOMATIC validation guard at work.
                  A high count here means the guard correctly rejected
                  bad TradingView data updates — it is a HEALTH signal,
                  not a problem. */}
              <div
                role="note"
                data-testid="dq-suspicious-info"
                className="mb-3 rounded-md border border-emerald-500/30 bg-emerald-50 dark:bg-emerald-950/20 px-3 py-2 text-xs text-emerald-900 dark:text-emerald-200"
              >
                <span className="font-semibold">Validation guard active.</span>{" "}
                Items below are TradingView updates that our guard rejected
                because they failed sanity checks (e.g. EPS swing &gt; 50%, PE &lt; 2
                or &gt; 200, etc.). A high count means the guard is doing its
                job — bad upstream data is being kept OUT of the screener.
                Each row can be approved (force-apply) or dismissed.
              </div>
              {loading.suspicious ? (
                <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
              ) : (
                <SuspiciousTable rows={suspicious} onAction={handleAction} onOpenOverride={handleOpenOverride} busy={actionBusy} />
              )}
            </TabsContent>

            <TabsContent value="overrides" className="pt-3">
              {loading.overrides ? (
                <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
              ) : (
                <ManualOverridesTable
                  rows={overrides}
                  onClearLock={handleClearLock}
                  busy={actionBusy}
                  onAddNew={() => handleOpenOverride(null, "corporate_action")}
                />
              )}
            </TabsContent>

            <TabsContent value="needs_eps" className="pt-3">
              {loading.corp ? (
                <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
              ) : (
                <CorporateActionSuspectsTable
                  rows={corporateActionSuspects}
                  onOpenOverride={(sym) => handleOpenOverride(sym, "corporate_action")}
                  busy={actionBusy}
                />
              )}
            </TabsContent>

            <TabsContent value="needs_reocr" className="pt-3">
              {loading.reocr ? (
                <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
              ) : (
                <OcrReextractRequiredTable
                  rows={ocrReextractRequired}
                  onReextract={handleReextract}
                  busy={actionBusy}
                />
              )}
            </TabsContent>

            <TabsContent value="auto" className="pt-3">
              {loading.auto ? (
                <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
              ) : (
                <AutoCorrectedTable rows={auto} />
              )}
            </TabsContent>

            <TabsContent value="audits" className="pt-3">
              {loading.audits ? (
                <div className="p-6 text-center text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
              ) : (
                <AuditHistoryTable rows={audits} />
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      <ManualOverrideDialog
        open={overrideOpen}
        symbol={overrideSymbol}
        defaultReason={overrideDefaultReason}
        onClose={() => { setOverrideOpen(false); setOverrideDefaultReason(null); }}
        onSuccess={refreshAll}
      />
    </div>
  );
};

export default AdminDataQualityPage;
